/**
 * New Leaf Cleaning's own footer content.
 *
 * WHY THIS IS ITS OWN FILE. Every line here is New Leaf's: its tagline, its
 * founding date, its market, its residential service menu, its opening hours,
 * and its W-2 employment claim. All of it used to sit inline in
 * `public-footer.tsx`, which renders on every public page of EVERY tenant — so
 * a commercial cleaner's visitors were told the company breathes life back into
 * your home, does residential cleaning across Greater Tampa Bay, and was
 * founded in December 2025.
 *
 * Separating it makes the invariant mechanically checkable rather than a matter
 * of remembering: the shared footer imports no `SITE`, and this component is
 * rendered behind a single `branding.isNewLeaf` gate. The unit test in
 * `tests/unit/tenant-brand-leak.test.ts` enforces both halves.
 *
 * This is the sanctioned exception in the house rule — a company's OWN branding
 * in its OWN content. It becomes a violation the moment it renders for anyone
 * else, which is what the gate prevents.
 *
 * When New Leaf's marketing site moves out of this repo (see `~/data/site`),
 * this file leaves with it and the shared footer needs no change.
 */

import { Link } from 'react-router';
import { SITE } from '~/data/site';

/** The brand block's descriptive lines — tagline, market, employment model. */
export function NewLeafFooterBlurb() {
  return (
    <>
      <p className="foot-tagline">{SITE.tagline}</p>
      <p className="foot-meta">Residential cleaning · {SITE.serviceArea}</p>
      <p className="foot-meta">W-2 employees only · Founded {SITE.founded}</p>
    </>
  );
}

/** The Services and Company link columns — every destination is an NLC page. */
export function NewLeafFooterColumns() {
  return (
    <>
      <div className="foot-col">
        <h2 className="footer-col-heading">Services</h2>
        <Link to="/services">Recurring cleaning</Link>
        <Link to="/services#deep">One-time deep clean</Link>
        <Link to="/services#moveout">Move-in &amp; move-out</Link>
        <Link to="/pricing">Pricing</Link>
      </div>

      <div className="foot-col">
        <h2 className="footer-col-heading">Company</h2>
        <Link to="/about">About us</Link>
        <Link to="/reviews">Reviews</Link>
        <Link to="/service-areas">Service areas</Link>
        <Link to="/policies">Policies</Link>
      </div>
    </>
  );
}

/** New Leaf's opening hours. A different company keeps different ones. */
export function NewLeafFooterHours() {
  return <span className="foot-hours">{SITE.contact.hours}</span>;
}

/** "St. Petersburg, FL", appended to the copyright line. */
export function NewLeafFooterLocation() {
  return <> · {SITE.location}</>;
}
